package dev.lingvo.site.components.editor

import kotlin.math.abs

internal interface EditorViewEffectNode {
    val connected: Boolean
    val clientHeight: Double
    val scrollHeight: Double
    var scrollTop: Double
    fun focus()
}

internal interface EditorViewEffectEnvironment {
    fun resolve(ref: EditorFocusRef): EditorViewEffectNode?
    fun queueScroller(): EditorViewEffectNode?
    fun activeNode(): EditorViewEffectNode?
    suspend fun awaitAnimationFrame()
}

internal interface EditorFocusSelectionRestorer {
    fun consumeAndRestoreSelection(target: EditorFocusRef.Target)
    fun restoreSelection(snapshot: EditorFocusRef.TargetSnapshot) = Unit
}

private fun EditorFocusSelectionRestorer.restoreSelection(ref: EditorFocusRef) {
    when (ref) {
        is EditorFocusRef.Target -> consumeAndRestoreSelection(ref)
        is EditorFocusRef.TargetSnapshot -> restoreSelection(ref)
        else -> Unit
    }
}

private suspend fun <T> retryForThreeFrames(
    read: () -> T?,
    environment: EditorViewEffectEnvironment,
): T? {
    read()?.let { return it }
    repeat(3) {
        environment.awaitAnimationFrame()
        read()?.let { return it }
    }
    return null
}

internal suspend fun runEditorViewEffect(
    effect: EditorViewEffect,
    environment: EditorViewEffectEnvironment,
    queueMounted: Boolean,
): EditorViewEffectResult = when (effect) {
    is EditorViewEffect.RestoreQueueScroll -> {
        if (!queueMounted) return EditorViewEffectResult(
            effect.id,
            EditorViewEffectOutcome.SUPERSEDED,
            "queue-not-mounted",
        )
        val scroller = retryForThreeFrames(
            { environment.queueScroller()?.takeIf(EditorViewEffectNode::connected) },
            environment,
        ) ?: return EditorViewEffectResult(
            effect.id,
            EditorViewEffectOutcome.FAILED,
            "queue-missing",
        )
        val maximum = (scroller.scrollHeight - scroller.clientHeight).coerceAtLeast(0.0)
        scroller.scrollTop = effect.topPx.coerceIn(0.0, maximum)
        environment.awaitAnimationFrame()
        val expected = effect.topPx.coerceIn(0.0, maximum)
        if (abs(scroller.scrollTop - expected) > 1.0) {
            EditorViewEffectResult(effect.id, EditorViewEffectOutcome.FAILED, "scroll-not-settled")
        } else {
            EditorViewEffectResult(effect.id, EditorViewEffectOutcome.COMPLETED, "scroll:$expected")
        }
    }

    is EditorViewEffect.Focus -> {
        val exact = retryForThreeFrames(
            { environment.resolve(effect.target)?.takeIf(EditorViewEffectNode::connected) },
            environment,
        )
        val resolved = exact ?: effect.fallback?.let { fallback ->
            retryForThreeFrames(
                { environment.resolve(fallback)?.takeIf(EditorViewEffectNode::connected) },
                environment,
            )
        }
        if (resolved == null) {
            (environment as? EditorFocusSelectionRestorer)?.restoreSelection(effect.target)
            EditorViewEffectResult(effect.id, EditorViewEffectOutcome.FAILED, "focus-missing")
        } else {
            try {
                resolved.focus()
            } finally {
                (environment as? EditorFocusSelectionRestorer)?.restoreSelection(effect.target)
            }
            environment.awaitAnimationFrame()
            if (environment.activeNode() == resolved) {
                EditorViewEffectResult(
                    effect.id,
                    EditorViewEffectOutcome.COMPLETED,
                    if (exact == null) {
                        "fallback:${effect.fallback?.evidenceName().orEmpty()}"
                    } else {
                        "target:${effect.target.evidenceName()}"
                    },
                )
            } else {
                EditorViewEffectResult(effect.id, EditorViewEffectOutcome.FAILED, "focus-not-settled")
            }
        }
    }
}

internal fun failedEditorViewEffectResult(
    effectId: Long,
    cause: Throwable,
): EditorViewEffectResult {
    val detail = runCatching { cause.message?.trim().orEmpty() }
        .getOrDefault("")
        .ifBlank { "effect-failed" }
    return EditorViewEffectResult(effectId, EditorViewEffectOutcome.FAILED, detail)
}

internal fun oldestRunnableEditorEffect(
    disposition: EditorMeasuredLayoutDisposition,
    layoutMeasurementId: Long,
    postCommitEvidence: EditorPostCommitEvidenceState,
    pending: List<EditorViewEffect>,
): EditorViewEffect? = when {
    disposition == EditorMeasuredLayoutDisposition.MEASURING -> null
    disposition == EditorMeasuredLayoutDisposition.WORKBENCH ||
        disposition == EditorMeasuredLayoutDisposition.COMPACT_FALLBACK -> pending.firstOrNull()
        ?.takeIf { postCommitEvidence.permitsSettledEffects(layoutMeasurementId) }
    else -> pending.firstOrNull()
}

internal fun acknowledgeEditorViewEffect(
    pending: List<EditorViewEffect>,
    result: EditorViewEffectResult,
): List<EditorViewEffect> = if (pending.firstOrNull()?.id == result.effectId) {
    pending.drop(1)
} else {
    pending
}

internal data class EditorPostCommitEvidenceState(
    val layoutMeasurementId: Long? = null,
    val measurementChainStartId: Long? = null,
    val probeStartRecorded: Boolean = true,
    val settledResultRecorded: Boolean = true,
    val terminalAbandonmentPending: Boolean = false,
    val abandonedFallbackCommitted: Boolean = false,
) {
    fun beginMeasurement(id: Long): EditorPostCommitEvidenceState = copy(
        layoutMeasurementId = id,
        measurementChainStartId = if (
            settledResultRecorded || abandonedFallbackCommitted || measurementChainStartId == null
        ) {
            id
        } else {
            measurementChainStartId
        },
        probeStartRecorded = false,
        settledResultRecorded = false,
        terminalAbandonmentPending = false,
        abandonedFallbackCommitted = false,
    )

    fun recordProbeStart(id: Long): EditorPostCommitEvidenceState = if (
        layoutMeasurementId == id
    ) {
        copy(probeStartRecorded = true)
    } else {
        this
    }

    fun recordSettledResult(id: Long): EditorPostCommitEvidenceState = if (
        layoutMeasurementId == id && probeStartRecorded
    ) {
        copy(
            settledResultRecorded = true,
            terminalAbandonmentPending = false,
        )
    } else {
        this
    }

    fun beginTerminalAbandonment(id: Long): EditorPostCommitEvidenceState = if (
        layoutMeasurementId == id && !settledResultRecorded
    ) {
        copy(terminalAbandonmentPending = true)
    } else {
        this
    }

    fun recordAbandonedFallbackCommit(id: Long): EditorPostCommitEvidenceState = if (
        layoutMeasurementId == id && terminalAbandonmentPending
    ) {
        copy(
            terminalAbandonmentPending = false,
            abandonedFallbackCommitted = true,
        )
    } else {
        this
    }

    fun permitsObserverResult(id: Long): Boolean = layoutMeasurementId == id && probeStartRecorded

    fun permitsSettledEffects(id: Long): Boolean = layoutMeasurementId == id &&
        ((probeStartRecorded && settledResultRecorded) || abandonedFallbackCommitted)
}

internal fun EditorPostCommitEvidenceState.measurementRestartBudgetExceeded(id: Long): Boolean =
    id - (measurementChainStartId ?: id) >= EDITOR_MEASUREMENT_RESTART_BUDGET
